// src/components/vendorChat/VendorChat.tsx
import { useState, useRef, useEffect } from 'react';
import {
  useBreakpointValue,
  Box,
  Grid,
  GridItem,
  Text,
  Flex,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import ChatIcon from '../../../assets/svg/chat.svg?react';
import Button from '../Button';
import X from '@/assets/svg/x.svg?react';

import { useQueryClient } from 'react-query';
import { IChat, IMessage } from '@/services/message/types';
import { useGetChatQuery, useGetChatsQuery } from '@/services/message';
import { vendorChatWebSocket } from '@/services/message/websocket';
import VendorChatSidebar from './ChatSidebar';
import VendorMessageArea from './MessageArea';

const Chat: React.FC = () => {
  const [showChatOverlay, setShowChatOverlay] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [displayMessageArea, setDisplayMessageArea] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const buttonText = useBreakpointValue({ base: undefined, lg: 'Messages' });

  const {
    data: chatListResponse,
    isLoading: listLoading,
    isError: listError,
  } = useGetChatsQuery(1, 20);
  const chatList = chatListResponse?.results || [];

  const {
    data: selectedChat,
    isLoading: chatLoading,
    isError: chatError,
  } = useGetChatQuery(selectedChatId);

  const handleChatClick = () => setShowChatOverlay(!showChatOverlay);
  const handleChatItemClick = (chat: IChat) => {
    setSelectedChatId(chat.id);
    setDisplayMessageArea(true);
  };

  const sendMessage = (content: string) => {
    if (!selectedChatId || !selectedChat) return;
    const receiverId = selectedChat.user.id;

    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: IMessage = {
      id: tempId,
      chat: selectedChatId,
      sender: selectedChat.vendor.user,
      receiver: selectedChat.user,
      message_type: 'text',
      content,
      image: null,
      image_small: null,
      image_medium: null,
      audio: null,
      video: null,
      is_read: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    queryClient.setQueryData<IChat | undefined>(['vendor-chat', selectedChatId!], (old) => {
      if (!old) return old;
      return {
        ...old,
        messages: [...old.messages, optimisticMessage],
      };
    });

    vendorChatWebSocket.sendMessage(selectedChatId, content, receiverId);
  };

  const readMessage = (messageId: string) => {
    if (!selectedChat) return;
    const updated = {
      ...selectedChat,
      messages: selectedChat.messages.map((m) =>
        m.id === messageId ? { ...m, is_read: true } : m,
      ),
    };
    queryClient.setQueryData(['vendor-chat', selectedChatId!], updated);
  };

  const hasUnread = chatList.some((chat) =>
    chat.messages.some((m) => m.sender?.user_type === 'user' && !m.is_read),
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) setShowChatOverlay(false);
    };
    if (showChatOverlay) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showChatOverlay]);

  const gridWidth = { base: '100%', lg: '40vw' };
  const gridMinWidth = '350px';

  return (
    <>
      <Box
        position="fixed"
        inset={0}
        bg="rgba(0,0,0,0.6)"
        backdropFilter="blur(2px)"
        opacity={showChatOverlay ? 1 : 0}
        pointerEvents={showChatOverlay ? 'auto' : 'none'}
        transition="opacity .3s"
        zIndex="overlay"
      />

      {!showChatOverlay && (
        <Button
          onClick={handleChatClick}
          variant="primary"
          position="fixed"
          bottom="4"
          right="4"
          zIndex="overlay"
          color="white"
          size="lg"
          w={{ base: '52px', lg: '134px' }}
          h="50px"
          shadow="lg"
          borderRadius="xl"
          leftIcon={
            <Box position="relative" w="24px" h="24px">
              <Icon as={ChatIcon} boxSize="24px" />
              {hasUnread && (
                <Box
                  bg="#fff"
                  h={2}
                  w={2}
                  borderRadius="full"
                  position="absolute"
                  top={0}
                  right={0}
                  border="2px solid"
                  borderColor="brand.primary.DEFAULT"
                />
              )}
            </Box>
          }
          fontSize="sm"
          fontFamily="secondary"
          fontWeight="semibold"
          px="14px"
          py="8px"
          justifyContent={buttonText ? 'flex-start' : 'center'}
          boxShadow="0px 6px 20px -5px #A71C29"
        >
          {buttonText}
        </Button>
      )}

      {showChatOverlay && (
        <Grid
          ref={chatRef}
          position="fixed"
          bottom={{ base: 0, lg: '10' }}
          right={{ base: 0, lg: '10' }}
          w={gridWidth}
          h={{ base: '75vh', lg: '500px' }}
          minW={gridMinWidth}
          minH="500px"
          bg="white"
          shadow="lg"
          borderRadius="md"
          zIndex="overlay"
          templateColumns={{ base: '1fr', lg: '2fr 3fr' }}
          overflow="hidden"
        >
          {listLoading ? (
            <GridItem colSpan={2}>
              <Flex h="100%" align="center" justify="center">
                <Spinner size="xl" />
              </Flex>
            </GridItem>
          ) : listError ? (
            <GridItem colSpan={2}>
              <Flex h="100%" align="center" justify="center" direction="column">
                <Text color="red.500">Failed to load chat list</Text>
              </Flex>
            </GridItem>
          ) : chatList.length === 0 ? (
            <GridItem colSpan={2}>
              <Flex h="100%" align="center" justify="center">
                <Text color="text.disable" fontSize="sm" fontFamily="secondary">
                  No chats available
                </Text>
              </Flex>
            </GridItem>
          ) : (
            <>
              <GridItem
                colSpan={1}
                borderRight={{ base: 'none', lg: '1px solid rgba(208,213,221,0.6)' }}
                display={{ base: displayMessageArea ? 'none' : 'block', lg: 'block' }}
              >
                <VendorChatSidebar
                  handleChatItemClick={handleChatItemClick}
                  selectedChat={selectedChat || null}
                />
              </GridItem>

              <GridItem
                colSpan={1}
                display={{ base: displayMessageArea ? 'block' : 'none', lg: 'block' }}
              >
                {chatLoading ? (
                  <Flex h="100%" align="center" justify="center">
                    <Spinner />
                  </Flex>
                ) : chatError ? (
                  <Flex h="100%" align="center" justify="center">
                    <Text color="red.500">Failed to load messages</Text>
                  </Flex>
                ) : selectedChat ? (
                  <VendorMessageArea
                    chat={selectedChat}
                    chatId={selectedChatId!}
                    sendMessage={sendMessage}
                    readMessage={readMessage}
                    setDisplayMessageArea={setDisplayMessageArea}
                  />
                ) : (
                  <Flex h="100%" align="center" justify="center">
                    <Text color="text.disable" fontSize="sm" fontFamily="secondary">
                      Select a chat to see messages
                    </Text>
                  </Flex>
                )}
              </GridItem>
            </>
          )}

          <Box
            as="button"
            onClick={() => {
              setSelectedChatId(null);
              setDisplayMessageArea(false);
              setShowChatOverlay(false);
            }}
            position="absolute"
            top="1rem"
            right="1rem"
          >
            <Icon as={X} boxSize="18px" />
          </Box>
        </Grid>
      )}
    </>
  );
};

export default Chat;
